import React, {
	ColumnDef,
	useReactTable,
	getCoreRowModel,
	getFilteredRowModel,
	SortingState,
	getSortedRowModel,
	AccessorKeyColumnDef,
	ColumnFiltersState,
} from '@tanstack/react-table'
import { RefObject, useMemo, useState } from 'react'
import {
	Delete,
	Edit,
	FileDownloadOutlined,
	FilterAltOff,
	InfoOutlined,
	IosShareOutlined,
	Key,
	// KeyboardArrowDown,
	// KeyboardArrowUp,
	Link,
	LocalOffer,
	Lock,
	ManageHistory,
	Star,
	StarOutlineOutlined,
	// UnfoldMore,
	Visibility,
} from '@mui/icons-material'
import { getFileNameAndExt } from '../../lib/utils/formatDocument'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useUserDateFormat } from '../useUserDateFormat'
import { useMediaQuery } from 'react-responsive'
import { DocumentWithText } from '../../lib/api/types/document.interfaces'
import {
	Checkbox,
	EllipsisText,
	IconButton,
	OptionsMenuButton,
	Tooltip,
} from '../../components/ui'
import { ReactComponent as ResetColumnsSize } from '../../assets/icons/table/reset-columns.svg'
import {
	openAuraEdit,
	useToggleObjectFavorite,
} from '../api/documentHooks'
import cn from '../../lib/utils/cn'
import { MainLayoutType } from '../../components/layouts/MainLayout'
import {
	EditingItem,
	DataTableEntry,
	CustomColumnFiltersState,
} from '../../pages/Workspace/components/interfaces.table'
import {
	formatDocumentSize,
	processingStatuses,
	// processingStatusesShort,
	shortenFileName,
} from '../../lib/utils/formatDocument'
// import FilterButton, {
// 	FILTERED_OBJECT_TYPE_VALUES,
// 	FilteredObjectType,
// } from '../../pages/Workspace/components/FilterButton'
import FileFormatIcon from '../../components/common/FileFormatIcon'
import {
	useCustomGlobalFilter,
	useDateFilter,
	useSizeFilter,
	useTextFilter,
} from './useColumnFilters'
import ProcessingStatusIcon from '../../pages/Workspace/components/ProcessingStatusIcon'
import { Features, useBilling } from '../../context/billingContext'
import buildSnippetAroundHighlight from '../../lib/utils/files/buildSnippetAroundHighlight'
import { ReactComponent as SigningSymbol } from '../../assets/icons/interactions/signing-symbol.svg'
import { useAuth } from '../../hooks'

export type DataTableEntryWithText = DataTableEntry & { text?: string }

interface useCreateSearchResultsDataTableProps {
	data?: DocumentWithText[] | null
	// visibleColumns: string[]
	containerRef?: RefObject<HTMLDivElement>
	searchQuery?: string
	onToggleLock: (id: string, value: boolean) => void
	onToggleItem?: (
		id: string,
		type: number,
		checked: boolean,
		name?: string,
		fileType?: string
	) => void
	onToggleAll?: (
		ids: { id: string; type: number; name?: string }[],
		checked: boolean
	) => void
	onEditItem: (item: EditingItem) => void
	onDeleteItem: (item: EditingItem) => void
	onPreview: (id: string, versionId?: string | null, isBlank?: boolean) => void
	onFileInfo: (id: string) => void
	onConfigPermissions: (id: string) => void
	onConfigVersion: (id: string) => void
	onDownload: (id: string) => void
	onCopyLink?: (id: string, type?: number) => void
	onSendToESign?: (id: string) => void
	onShareItem: (item: EditingItem) => void
	checkedItems: {
		id: string
		type: number
	}[]
	lockedFiles: Record<string, boolean>
	isMobile?: boolean
}

const customGlobalFilter = useCustomGlobalFilter()
const dateFilter = useDateFilter()
const sizeFilter = useSizeFilter()
const textFilter = useTextFilter()

export const useCreateSearchResultsDataTable = ({
	data = [],
	// visibleColumns,
	containerRef,
	searchQuery,
	onToggleLock,
	onToggleItem,
	onToggleAll,
	onDeleteItem,
	onEditItem,
	onPreview,
	onFileInfo,
	onConfigPermissions,
	onConfigVersion,
	onDownload,
	onCopyLink,
	onSendToESign,
	onShareItem,
	checkedItems,
	lockedFiles,
	isMobile,
}: useCreateSearchResultsDataTableProps) => {
	const navigate = useNavigate()
	const { hasFeature } = useBilling()
	const { formatDate, formatDateTime } = useUserDateFormat()

	const { currentUser } = useAuth()
	const { isApp } = useOutletContext<MainLayoutType>()
	const hideESignActions = Boolean(isApp || isMobile)

	const [sorting, setSorting] = useState<SortingState>([])
	const [columnFilters, setColumnFilters] = useState<CustomColumnFiltersState>(
		[]
	)
	const { mutateAsync: toggleFavorite } = useToggleObjectFavorite()

	const handleEditDocument = (fileId: string, fileName?: string) => {
		openAuraEdit(fileId, fileName)
	}

	const handleToggleFavorite = async (item: {
		id: string
		type: number
		value: boolean
	}) => {
		await toggleFavorite({ data: item, invalidate: true })
	}

	const renderSortingHeader = (
		title: string,
		column: React.Column<DataTableEntryWithText, unknown>,
		className?: string
	) => {
		return (
			<div
				className={cn(
					'w-full flex group justify-start items-center gap-1 cursor-pointer select-none h-5 group pl-4 box-border dark:text-text-table-dark text-text-table',
					className
				)}
			>
				{/* <FilterButton
					columnKey={column?.id}
					setColumnFilters={setColumnFilters}
					columnFilters={columnFilters}
					isDate={['createdAt', 'modifiedAt']?.includes(column?.id)}
					isSize={column?.id === 'size'}
					objectType={
						['modifiedBy', 'createdBy']?.includes(column?.id)
							? 'user'
							: FILTERED_OBJECT_TYPE_VALUES?.includes(
									column?.id as FilteredObjectType
							  )
							? (column.id as FilteredObjectType)
							: null
					}
					checkboxesItems={
						column?.id === 'processingStatus'
							? Object?.entries(processingStatuses)?.map(([k, v]) => ({
									id: k,
									label: v,
							  }))
							: null
					}
				/> */}
				<EllipsisText
					tooltipLabel={title}
					text={title}
					className="text-center w-fit max-w-auto truncate dark:text-text-table-dark text-text-table"
				/>
				{/* <span
					className={cn(
						'flex items-center justify-center transition-all duration-200 w-3 h-5 dark:text-text-table-dark text-text-table',
						column.getIsSorted() === false && 'hidden group-hover:inline-block'
					)}
				>
					{column.getIsSorted() === 'asc' && (
						<KeyboardArrowUp className="w-5 h-5" />
					)}
					{column.getIsSorted() === 'desc' && (
						<KeyboardArrowDown className="w-5 h-5" />
					)}
					{column.getIsSorted() === false && (
						<UnfoldMore className="h-5 w-5 opacity-0 group-hover:opacity-100 duration-default -ml-1" />
					)}
				</span> */}
			</div>
		)
	}

	const tableData = useMemo(() => {
		const safeData = Array.isArray(data) ? data : []
		return safeData?.map((entry) => ({
			id: entry.id,
			name: entry.name,
			text: entry.text ?? '-',
			createdBy: entry?.createdBy?.name || entry?.createdBy?.email,
			createdAt: entry?.createdAt || null,
			type: entry?.fileType,
			objectType: entry?.type,
			size: entry?.size,
			category: entry?.category?.name
				? entry?.category?.name
				: '-',
			subcategory: entry?.subcategory?.name
				? entry?.subcategory?.name
				: '-',
			modifiedBy: entry?.modifiedBy?.name || entry?.modifiedBy?.email,
			modifiedAt: entry?.modifiedAt,
			processingStatus:
				entry?.processingStatus === 0 ? 2 : entry?.processingStatus || -1,
			lock: entry?.lock,
			options: {
				isFavorite: entry?.isFavorite,
				isLocked: Boolean(
					typeof lockedFiles[entry?.id] === 'boolean'
						? lockedFiles[entry?.id]
						: entry?.lock?.id
				),
				isSigned: entry?.isSigned,
			},
		}))
	}, [data, lockedFiles])

	const columns = useMemo<ColumnDef<DataTableEntryWithText>[]>(() => {
		const base: ColumnDef<DataTableEntryWithText>[] = [
			{
				accessorKey: 'name',
				enableResizing: true,
				enableSorting: true,
				minSize: 140,
				size: isMobile ? 160 : 100,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => {
					const rows = table.getRowModel().rows
					const allIds = rows
						?.filter(
							(row) =>
								!lockedFiles[row?.original?.id] &&
								!row?.original?.options?.isLocked
						)
						.map((row) => ({
							id: row.original.id,
							type: row.original.type,
							name: row.original.name,
						}))

					const isAllSelected = Boolean(
						allIds?.length &&
						allIds?.every(({ id }) =>
							checkedItems?.find((item) => item?.id === id)
						)
					)
					return (
						<div className="h-full flex items-center gap-3 max-w-full pl-3 md:pl-6 group cursor-pointer">
							<Checkbox
								checked={isAllSelected}
								disabled={!allIds?.length}
								onChange={() => onToggleAll?.(allIds, !isAllSelected)}
							/>
							{renderSortingHeader('Name', column, 'w-fit pl-2')}
						</div>
					)
				},
				cell: ({ row }) => {
					const name = row?.original?.name ? String(row?.original?.name) : ''
					const isChecked = checkedItems?.some(
						({ id }) => id === row?.original?.id
					)

					const isLocked = row?.original?.options?.isLocked
					const isSigned = row?.original?.options?.isSigned

					return (
						<div className="flex items-center gap-2 max-w-fit pl-3 md:pl-6 overflow-ellipsis">
							{isLocked ? (
								<IconButton
									variant="secondary"
									className="w-5 h-5 min-w-5 min-h-5 mr-3 rounded-md p-0 bg-background-light-hover hover:bg-background-light-hover"
									icon={<Lock className="w-3.5 h-3.5 fill-text-secondary" />}
									tooltip={
										row?.original?.lock?.createdBy?.id
											? `Locked by ${row?.original?.lock?.createdBy?.id === currentUser?.id
												? 'you'
												: row?.original?.lock?.createdBy?.name ??
												row?.original?.lock?.createdBy?.email
											}`
											: 'Unlock'
									}
									onClick={() => {
										if (
											row?.original?.lock?.id === currentUser?.id ||
											currentUser?.organization?.isAdministrator
										) {
											onToggleLock(row?.original?.id, false)
										}
									}}
									disabled={
										currentUser?.id !== row.original?.lock?.createdBy?.id &&
										!currentUser?.organization?.isAdministrator
									}
								/>
							) : (
								<Checkbox
									checked={isChecked}
									disabled={isLocked}
									onChange={(_, event) => {
										event?.stopPropagation()
										onToggleItem?.(
											row.original.id,
											row.original.objectType,
											!isChecked,
											row?.original?.name,
											row?.original?.type
										)
									}}
									className="mr-3"
								/>
							)}
							<FileFormatIcon
								className="w-5 h-5"
								extension={getFileNameAndExt(name).ext}
								isFolder={!!row?.original?.objectType}
								isSigned={isSigned}
							/>
							<Tooltip label={name} className={cn(isMobile && 'opacity-0')}>
								<p className="text-text-main truncate dark:text-text-table-dark">
									{isMobile ? shortenFileName(name) : name}
								</p>
							</Tooltip>
						</div>
					)
				},
			},
			{
				accessorKey: 'text',
				enableResizing: true,
				enableSorting: true,
				minSize: 100,
				size: 100,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Text', column),
				cell: (info) => {
					const textContent = String(info.getValue() ?? '')
					const snippetHtml = buildSnippetAroundHighlight(
						textContent,
						70,
						false
					)

					return (
						<Tooltip
							label={
								<div
									dangerouslySetInnerHTML={{
										__html: snippetHtml,
									}}
								/>
							}
							className="w-[300px]"
						>
							<p
								className="truncate"
								dangerouslySetInnerHTML={{
									__html: textContent,
								}}
							/>
						</Tooltip>
					)
				},
			},
			{
				accessorKey: 'createdBy',
				enableResizing: true,
				enableSorting: true,
				minSize: 100,
				size: 100,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,

				header: ({ column }) => renderSortingHeader('Creator', column),
				cell: (info) => (
					<p className="truncate">{String(info.getValue() ?? '')}</p>
				),
			},
			{
				accessorKey: 'createdAt',
				enableResizing: true,
				enableSorting: true,
				minSize: 120,
				size: 120,
				sortingFn: 'datetime',
				filterFn: dateFilter,
				header: ({ column }) => renderSortingHeader('Creation Date', column),
				cell: (info) => {
					const val = info.getValue()
					if (!val) return '-'
					const str = String(val)
					return (
						<Tooltip label={formatDateTime(str)} classNameChildrenWrapper="w-fit">
							<span>{formatDate(str)}</span>
						</Tooltip>
					)
				},
			},
			{
				accessorKey: 'modifiedAt',
				enableResizing: true,
				enableSorting: true,
				minSize: 120,
				size: 120,
				sortingFn: 'datetime',
				filterFn: dateFilter,
				header: ({ column }) => renderSortingHeader('Modified Date', column),
				cell: (info) => {
					const val = info.getValue()
					if (!val) return '-'
					const str = String(val)
					return (
						<Tooltip label={formatDateTime(str)} classNameChildrenWrapper="w-fit">
							<span>{formatDate(str)}</span>
						</Tooltip>
					)
				},
			},
			{
				accessorKey: 'modifiedBy',
				enableResizing: true,
				minSize: 110,
				size: 110,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Modifier', column),
				cell: (info) => (
					<p className="truncate">{String(info.getValue() ?? '')}</p>
				),
			},
			{
				accessorKey: 'processingStatus',
				enableResizing: true,
				enableSorting: true,
				minSize: 120,
				size: 120,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Status', column),
				cell: ({ row }) => {
					const statusKey = Number(row.original.processingStatus)
					const { objectType, size } = row?.original
					return <ProcessingStatusIcon status={statusKey} isEmptyFile={!size} objectType={objectType} />
					// return row?.original?.objectType ? (
					// 	<Tooltip
					// 		label={"Folder can't have a status, but its items can"}
					// 		classNameChildrenWrapper="w-fit"
					// 	>
					// 		<InfoOutlined className="w-5 h-5" />
					// 	</Tooltip>
					// ) : statusKey in processingStatuses ? (
					// 	<Tooltip
					// 		label={processingStatuses[statusKey]}
					// 		classNameChildrenWrapper="w-fit"
					// 	>
					// 		<ProcessingStatusIcon status={statusKey} />
					// 	</Tooltip>
					// ) : (
					// 	'-'
					// )
				},
			},
			{
				accessorKey: 'category',
				enableResizing: true,
				minSize: 110,
				size: 110,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Category', column),
				cell: (info) => (
					<Tooltip
						label={String(info.getValue() ?? '')}
						className={cn(isMobile && 'opacity-0')}
					>
						<p className="capitalize truncate">
							{String(info.getValue() ?? '')}
						</p>
					</Tooltip>
				),
			},
			{
				accessorKey: 'subcategory',
				enableResizing: true,
				minSize: 130,
				size: 130,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Sub Category', column),
				cell: (info) => (
					<Tooltip
						label={String(info.getValue() ?? '')}
						className={cn(isMobile && 'opacity-0')}
					>
						<p className="capitalize truncate">
							{String(info.getValue() ?? '')}
						</p>
					</Tooltip>
				),
			},
			{
				accessorKey: 'type',
				enableResizing: true,
				minSize: 100,
				size: 100,
				enableSorting: true,
				sortingFn: 'alphanumeric',
				filterFn: textFilter,
				header: ({ column }) => renderSortingHeader('Type', column),
				cell: (info) => String(info.getValue() ?? ''),
			},
			{
				accessorKey: 'size',
				enableResizing: true,
				enableSorting: true,
				minSize: 80,
				size: 80,
				sortingFn: 'alphanumeric',
				filterFn: sizeFilter,
				header: ({ column }) => renderSortingHeader('Size', column),
				cell: (info) =>
					String(formatDocumentSize(Number(info.getValue())) || '-'),
			},
			{
				accessorKey: 'options',
				enableResizing: false,
				enableSorting: false,
				size: isMobile ? 100 : columnFilters?.length ? 50 : 30,
				maxSize: 50,
				minSize: 25,
				header: () => (
					<div className="flex items-center justify-end gap-1">
						<IconButton
							variant="transparent"
							className="w-8 h-8 group hover:bg-transparent"
							tooltipPosition="left"
							icon={
								<ResetColumnsSize className="fill-text-table w-5 h-5 group-hover:fill-accent-hover" />
							}
							onClick={() => table.resetColumnSizing()}
							tooltip={'Reset columns sizes'}
						/>
						{!!columnFilters?.length && (
							<IconButton
								variant="transparent"
								className="w-8 h-8 group hover:bg-transparent"
								tooltipPosition="left"
								icon={
									<FilterAltOff className="fill-text-table w-5 h-5 group-hover:fill-accent-hover" />
								}
								onClick={() => setColumnFilters([])}
								tooltip={'Clean filters'}
							/>
						)}
					</div>
				),
				cell: ({ row }) => {
					const { id, isFavorite, type, name, isLocked, size, isSigned } = {
						id: row.original.id,
						isFavorite: row.original?.options?.isFavorite,
						type: row?.original?.objectType,
						name: row?.original?.name,
						isLocked:
							row?.original?.options?.isLocked || lockedFiles[row.original.id],
						size: row?.original?.size,
						isSigned: row?.original?.options?.isSigned,
					}

					const isFileHasContent = Boolean(!type && size > 0)
					const isCommonItem = Boolean(type || isFileHasContent)

					return (
						<div className="bg-inherit md:absolute right-0 flex items-center justify-end gap-2 w-fit md:ml-auto pl-5 h-full group-hover:lg:left-custom-shadow">
							<div className="items-center justify-end hidden md:flex gap-2 opacity-0 lg:group-hover:opacity-100 transition-all duration-150 ease-in-out">
								{(isCommonItem
									? hasFeature(Features.Basic)
									: !type && !size && !isApp && hasFeature(Features.Versions)) && (
										<IconButton
											className="w-8 h-8 rounded-lg"
											variant="secondary"
											icon={
												isCommonItem || !hasFeature(Features.Versions) ? (
													<Visibility className="w-4 dark:fill-text-main-dark" />
												) : (
													<Edit className="w-4 dark:fill-text-main-dark" />
												)
											}
											onClick={() =>
												!type && !size && !isApp && hasFeature(Features.Versions)
													? handleEditDocument(
															row.original.id,
															row.original.name
														)
													: onPreview(id)
											}
											tooltip={
												type
													? 'Open folder'
													: size
														? 'Preview file'
														: hasFeature(Features.Versions)
															? 'Edit file'
															: 'Preview file'
											}
										/>
									)}
								{hasFeature(Features.Basic) && (
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant={isFavorite ? 'primary' : 'secondary'}
										icon={<Star className="w-4 dark:fill-text-main-dark" />}
										tooltip={
											isFavorite ? 'Remove from favorites' : 'Add to favorites'
										}
										onClick={() => {
											handleToggleFavorite({
												id,
												type,
												value: !isFavorite,
											})
										}}
									/>
								)}
								{hasFeature(Features.Basic) && (
									<IconButton
										className="w-8 h-8 rounded-lg"
										variant="secondary"
										icon={<Delete className="w-4 dark:fill-text-main-dark" />}
										tooltip="Delete"
										disabled={isLocked}
										onClick={() =>
											onDeleteItem({
												id,
												name,
												type,
											})
										}
									/>
								)}
							</div>
							<OptionsMenuButton
								buttonClassName="ml-auto mr-10"
								parentScrollRef={containerRef}
								items={[
									{
										icon: <Visibility />,
										label: type ? 'Open folder' : 'Open in new tab',
										onClick: () => onPreview(id, null, true),
										visible:
											hasFeature(Features.Basic) && isCommonItem && !isApp,
									},
									{
										icon: <Visibility />,
										label: 'Open',
										onClick: () => onPreview(id, null),
										visible: hasFeature(Features.Basic) && isFileHasContent && isMobile,
									},
									{
										icon: <Edit />,
										label: 'Edit',
										onClick: () => handleEditDocument(id, name),
										visible: !type && hasFeature(Features.Versions) && !isApp && !isSigned,
										disabled: isLocked,
									},
									{
										icon: <LocalOffer />,
										label: 'Rename',
										onClick: () =>
											onEditItem({
												id,
												name,
												type,
											}),
										visible: true,
										disabled: isLocked,
									},
									{
										icon: <Delete />,
										label: 'Delete',
										onClick: () =>
											onDeleteItem({
												id,
												name,
												type,
											}),
										visible: true,
										disabled: isLocked,
										withDivider: true,
									},
									{
										icon: <InfoOutlined />,
										label: 'File info',
										onClick: () => onFileInfo(id),
										visible: !type,
									},
									{
										icon: <ManageHistory />,
										label: 'Versions',
										onClick: () => onConfigVersion(id),
										visible: hasFeature(Features.Versions) && isFileHasContent,
									},
									{
										icon: isFavorite ? <Star /> : <StarOutlineOutlined />,
										label: isFavorite ? 'Unstar' : 'Star',
										onClick: () =>
											handleToggleFavorite({
												id,
												type: row?.original?.objectType,
												value: !isFavorite,
											}),
										visible: true,
									},
									{
										icon: <Key />,
										label: 'Permissions',
										onClick: () => onConfigPermissions(id),
										visible: hasFeature(Features.ManageGroupsRolesPermissions),
										disabled: isLocked,
									},
									{
										icon: <IosShareOutlined />,
										label: 'Share',
										onClick: () => onShareItem({ id, name, type }),
										visible: hasFeature(Features.Basic) && isFileHasContent,
										withDivider: !type && isSigned,
									},
									{
										icon: (
											<SigningSymbol className="w-5 h-5 text-text-main dark:text-text-main-dark" />
										),
										label: 'Send to eSign',
										onClick: () => onSendToESign?.(id),
										visible:
											hasFeature(Features.Basic) &&
											!type &&
											!isSigned &&
											!hideESignActions,
										withDivider: true,
									},
									{
										icon: <FileDownloadOutlined />,
										label: 'Download',
										onClick: () => onDownload(id),
										visible: hasFeature(Features.Basic) && isCommonItem,
									},
									{
										icon: <Link />,
										label: 'Copy link',
										onClick: () => {
											onCopyLink?.(id, type)
										},
										disabled: !onCopyLink,
										visible: hasFeature(Features.Basic),
									},
								]}
							/>
						</div>
					)
				},
			},
		]

		if (isMobile) {
			return base.filter(
				(
					columnDef
				): columnDef is AccessorKeyColumnDef<DataTableEntryWithText, any> => {
					return (
						'accessorKey' in columnDef &&
						['id', 'name', 'options'].includes(columnDef.accessorKey)
					)
				}
			)
		}

		return base
	}, [data, checkedItems, lockedFiles, columnFilters, isMobile, hideESignActions, onSendToESign])

	const table = useReactTable({
		data: tableData,
		columns,
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getSortedRowModel: getSortedRowModel(),
		globalFilterFn: customGlobalFilter,
		manualSorting: false,
		enableSorting: true,
		enableSortingRemoval: true,
		onSortingChange: setSorting,
		onColumnFiltersChange: (filters) => {
			const newFilters: CustomColumnFiltersState = (
				filters as ColumnFiltersState
			).map((filter) => ({
				id: filter.id,
				type: (filter.value as any)?.type ?? 'contain',
				value:
					(filter.value as any)?.type === 'between'
						? {
							startValue: (filter.value as any)?.value?.startValue ?? null,
							endValue: (filter.value as any)?.value?.endValue ?? null,
						}
						: (filter.value as any)?.value ?? '',
			}))

			if (JSON.stringify(columnFilters) !== JSON.stringify(newFilters)) {
				setColumnFilters(newFilters)
			}
		},
		state: {
			sorting,
			columnFilters: useMemo(
				() =>
					columnFilters.map((filter) => ({
						id: filter.id,
						value: { type: filter.type, value: filter.value },
					})),
				[columnFilters]
			),
			globalFilter: searchQuery,
		},
	})

	// useEffect(() => {
	// 	table.getAllColumns().forEach((column) => {
	// 		table
	// 			.getColumn(column.id)
	// }, [visibleColumns, table])

	return { table }
}
